import CustomTooltip from '../../shared/CustomTooltip';
import {
    Box,
    ButtonGroup,
    Chip,
    IconButton,
    Typography,
    useMediaQuery,
} from '@mui/material';
import React, { MouseEvent, useState } from 'react';
import SetMenuModal from './SetMenuModal';
import { CustomButton } from '../../../layout/styledcomponents/CustomButton';
import { AddCircle, Delete, Edit } from '@mui/icons-material';
import { CustomIconButton } from '../../../layout/styledcomponents/CustomIconButton';
import { useGetSetMenusQuery } from '../../../app/Slices/SevenDayApiSlice';
import UserRole from '../../../shared/AuthRoles';
import { SetMenu } from '../../types/set-menu';
import SetMenuPreviewModal from './SetMenuPreviewModal';
import SetMenuDeleteModal from './SetMenuDeleteModal';
import GenericTable from '../../shared/GenericTable';
import { MRT_ColumnDef } from 'mantine-react-table';
import { capitalizeName, formatCurrencyShort } from '../../../shared/utils';

const SetMenus = () => {
    const loggedInUser = UserRole();
    const isMobile = useMediaQuery('(max-width: 770px)');
    const isIpad = useMediaQuery('(max-width: 1200px)');

    const { data: setMenus, isLoading } = useGetSetMenusQuery(undefined, {
        skip: !loggedInUser?.company,
    });

    const [modalOpen, setModalOpen] = useState(false);
    const [selectedSetMenu, setSelectedSetMenu] = useState<SetMenu | null>(
        null,
    );
    const [previewOpen, setPreviewOpen] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);

    const handleDelete = (
        e: MouseEvent<HTMLButtonElement>,
        setMenu: SetMenu,
    ) => {
        e.stopPropagation();
        setSelectedSetMenu(setMenu);
        setDeleteOpen(true);
    };

    const handleEdit = (e: MouseEvent<HTMLButtonElement>, setMenu: SetMenu) => {
        e.stopPropagation();
        setSelectedSetMenu(setMenu);
        setModalOpen(true);
    };

    const columns: MRT_ColumnDef<any>[] = [
        {
            accessorKey: 'name',
            header: 'Name',
            enableSorting: false,
        },
        {
            accessorKey: 'serviceType',
            header: 'Type',
            enableSorting: false,
            accessorFn: (row) => capitalizeName(row.serviceType),
        },
        {
            accessorKey: 'pricePerPerson',
            header: 'Price / Person',
            enableSorting: false,
            accessorFn: (row) => formatCurrencyShort(row.pricePerPerson),
        },
        {
            accessorKey: 'items',
            header: 'Items',
            enableSorting: false,
            size: 280,
            maxSize: 280,
            accessorFn: (row) =>
                `${row.items?.map((item: any) => item.name).join(', ')}`,
            Cell: ({ cell }) => {
                const value = cell.getValue<string>();
                return (
                    <CustomTooltip title={value}>
                        <Typography
                            variant='body3'
                            sx={{
                                display: 'block',
                                maxWidth: 280,
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                            }}
                        >
                            {value}
                        </Typography>
                    </CustomTooltip>
                );
            },
        },
        {
            accessorKey: 'modules',
            header: 'Available on',
            enableSorting: false,
            size: 280,
            maxSize: 280,
            accessorFn: (row) =>
                row.modules
                    ?.map((module: { name?: string }) => module.name)
                    .join(', ') ?? '',
            Cell: ({ cell }) => {
                const value = cell.getValue<string>();
                return (
                    <CustomTooltip title={value}>
                        <Typography
                            variant='body3'
                            sx={{
                                display: 'block',
                                maxWidth: 280,
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                            }}
                        >
                            {value}
                        </Typography>
                    </CustomTooltip>
                );
            },
        },
        {
            accessorKey: 'isActive',
            header: 'Active',
            enableSorting: false,
            Cell: ({ row }) => (
                <Chip
                    size='small'
                    label={row.original.isActive ? 'Active' : 'Inactive'}
                    color={row.original.isActive ? 'success' : 'default'}
                    variant='outlined'
                />
            ),
        },
    ];

    return (
        <>
            <Typography variant='h1'>Set Menus</Typography>
            <Box
                sx={{ display: 'flex', justifyContent: 'space-between', py: 2 }}
            >
                <Typography variant='body3' sx={{ color: 'text.primary' }}>
                    Create catering and beverage set menus that can be added to
                    experiences and guests can choose from.
                </Typography>
                {isMobile || isIpad ? (
                    <CustomIconButton
                        variant='contained'
                        color='primary'
                        onClick={() => setModalOpen(true)}
                    >
                        <AddCircle />
                    </CustomIconButton>
                ) : (
                    <CustomButton
                        variant='contained'
                        color='primary'
                        onClick={() => setModalOpen(true)}
                    >
                        New Set Menu
                    </CustomButton>
                )}
            </Box>
            <GenericTable
                columns={columns}
                data={setMenus ?? []}
                tableName='Set-Menus'
                loading={isLoading}
                enableRowSelection={false}
                renderTopToolbar={<></>}
                enableRowActions
                positionActionsColumn='last'
                renderRowActions={({ row }) => {
                    return (
                        <ButtonGroup size='small'>
                            <CustomTooltip title='Edit Set Menu'>
                                <IconButton
                                    size='small'
                                    color='primary'
                                    onClick={(e) => handleEdit(e, row.original)}
                                >
                                    <Edit />
                                </IconButton>
                            </CustomTooltip>
                            <CustomTooltip title='Delete Set Menu'>
                                <IconButton
                                    size='small'
                                    color='error'
                                    onClick={(e) =>
                                        handleDelete(e, row.original)
                                    }
                                >
                                    <Delete />
                                </IconButton>
                            </CustomTooltip>
                        </ButtonGroup>
                    );
                }}
                onRowClick={(row) => {
                    setSelectedSetMenu(row);
                    setPreviewOpen(true);
                }}
            />
            <SetMenuModal
                setMenu={selectedSetMenu}
                open={modalOpen}
                onClose={() => {
                    setModalOpen(false);
                    setSelectedSetMenu(null);
                }}
                onSaved={(saved) => {
                    setModalOpen(false);
                    setSelectedSetMenu(saved);
                    setPreviewOpen(true);
                }}
            />
            <SetMenuPreviewModal
                setMenu={selectedSetMenu}
                open={previewOpen}
                onClose={() => {
                    setPreviewOpen(false);
                    setSelectedSetMenu(null);
                }}
                handleEdit={() => {
                    setPreviewOpen(false);
                    setModalOpen(true);
                }}
            />
            <SetMenuDeleteModal
                open={deleteOpen}
                onClose={() => {
                    setDeleteOpen(false);
                    setSelectedSetMenu(null);
                }}
                setMenu={selectedSetMenu}
            />
        </>
    );
};

export default SetMenus;
